import { useState, useRef, useEffect } from 'react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export default function CityAutocomplete({ onSelect, placeholder = 'Search for a city...' }) {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSuggestions = (value) => {
        if (!value || value.length < 2) {
            setSuggestions([]);
            return;
        }
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?types=place&language=en&access_token=${MAPBOX_TOKEN}`;
        fetch(url)
            .then(r => r.json())
            .then(data => {
                setSuggestions(data.features || []);
                setOpen(true);
            })
            .catch(() => setSuggestions([]));
    };

    const handleChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    };

    const handleSelect = (feature) => {
        const city = feature.text;
        const [lng, lat] = feature.center;

        // Parse state and country from context
        let state = '';
        let country = '';
        for (const ctx of (feature.context || [])) {
            if (ctx.id.startsWith('region.')) state = ctx.text;
            if (ctx.id.startsWith('country.')) country = ctx.text;
        }

        setQuery(feature.place_name);
        setOpen(false);
        setSuggestions([]);
        onSelect({ city, state, country, lat, lng });
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <input
                ref={inputRef}
                className="org-detail-input"
                value={query}
                onChange={handleChange}
                placeholder={placeholder}
                autoComplete="off"
            />
            {open && suggestions.length > 0 && (() => {
                const rect = inputRef.current?.getBoundingClientRect();
                const wrapperRect = wrapperRef.current?.getBoundingClientRect();
                const offsetLeft = rect ? rect.left - wrapperRect.left : 0;
                const offsetWidth = rect ? rect.width : '100%';
                return (
                <ul style={{
                    position: 'absolute',
                    top: '100%',
                    left: offsetLeft,
                    width: offsetWidth,
                    background: '#2e2e2e',
                    color: 'white',
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    borderRadius: '0 0 6px 6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                }}>
                    {suggestions.map(feature => (
                        <li
                            key={feature.id}
                            onMouseDown={() => handleSelect(feature)}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                fontSize: '0.9rem',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#444'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            {feature.place_name}
                        </li>
                    ))}
                </ul>
                );
            })()}
        </div>
    );
}
